import { useState, useCallback, useEffect } from 'react';
import { BaseFormModal } from '@/components/BaseFormModal';
import { ConfirmModal } from '@/components/ConfirmModal';
import { KstDataGrid } from '@/components/KstDataGrid';
import { taskTourStopsColumns } from '../tour-management-config';
import { api } from '@/services/api-class';
import { App } from '@/services/app-compat';

interface AssociatedStopsModalProps {
  open: boolean;
  task: any; // The task whose associated stops we're showing (needs .id and .name)
  onClose: () => void;
  onAssignMore: () => void; // Called when user clicks "Assign to More Stops"
  onChanged: () => void; // Called after a stop is removed (to refresh parent grid)
}

export function AssociatedStopsModal({ open, task, onClose, onAssignMore, onChanged }: AssociatedStopsModalProps) {
  const [rows, setRows] = useState<{ entries: any[] | null }>({ entries: null });
  const [loading, setLoading] = useState(false);
  const [removeConfirm, setRemoveConfirm] = useState<{ open: boolean; tourStopId: number; title: string }>({ open: false, tourStopId: 0, title: '' });
  const [removing, setRemoving] = useState(false);

  const loadStops = useCallback(() => {
    if (!task?.id) return;
    setLoading(true);
    api.getTaskTourStops(task.id, (result: any) => {
      const entries = (result || []).map((row: any) => ({ ...row, id: row.tourStopId, isChecked: false }));
      setRows({ entries });
      setLoading(false);
    });
  }, [task?.id]);

  useEffect(() => {
    if (open && task?.id) {
      loadStops();
    }
  }, [open, task?.id, loadStops]);

  const handleRemoveClick = useCallback((_payload: { action: string; row: any }) => {
    setRemoveConfirm({ open: true, tourStopId: _payload.row.tourStopId, title: _payload.row.title });
  }, []);

  const handleConfirmRemove = useCallback(() => {
    setRemoving(true);
    api.removeTaskFromTourStop(task.id, removeConfirm.tourStopId, () => {
      setRemoving(false);
      setRemoveConfirm({ open: false, tourStopId: 0, title: '' });
      App.showPopupNotification('Task removed from tour stop.', 'success');
      loadStops();
      onChanged();
    });
  }, [task?.id, removeConfirm.tourStopId, loadStops, onChanged]);

  const handleCancelRemove = useCallback(() => {
    setRemoveConfirm({ open: false, tourStopId: 0, title: '' });
  }, []);

  const stopCount = rows.entries?.length ?? 0;
  const isEmpty = !loading && stopCount === 0;

  return (
    <>
      <BaseFormModal
        open={open}
        title={`Tour Stops with "${task?.name || ''}"`}
        dialogClass="kstc-dialog--assign-stops"
        saveText="Assign to More Stops"
        cancelText="Close"
        onSave={onAssignMore}
        onCancel={onClose}
      >
        {isEmpty ? (
          <p style={{ padding: '1rem', textAlign: 'center', color: 'var(--kst-text-secondary)' }}>
            This task is not assigned to any tour stops.
          </p>
        ) : (
          <>
            <p style={{ marginBottom: '0.5rem' }}>{stopCount} tour stop(s) assigned</p>
            <KstDataGrid
              columns={taskTourStopsColumns}
              rows={rows}
              loading={loading}
              showcheckbox={false}
              showheader={false}
              mobiletitlefield="title"
              rowactions={[{ name: 'Remove', event: 'remove', icon: 'fa-trash', class: 'btn-danger', iconOnly: true }]}
              onRowAction={handleRemoveClick}
            />
          </>
        )}
      </BaseFormModal>

      <ConfirmModal
        open={removeConfirm.open}
        title="Remove Task from Tour Stop"
        message={`Remove "${task?.name}" from "${removeConfirm.title}"?`}
        confirmText="Remove"
        confirmClass="danger"
        busy={removing}
        onConfirm={handleConfirmRemove}
        onCancel={handleCancelRemove}
      />
    </>
  );
}
